import type { Component, Ref } from 'vue'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import WidgetFavorites from '@/components/dashboard/WidgetFavorites.vue'
import WidgetInfoboard from '@/components/dashboard/WidgetInfoboard.vue'
import WidgetContentWorkTime from '@/components/dashboard/WidgetContentWorkTime.vue'
import WidgetQuickStateChange from '@/components/dashboard/WidgetQuickStateChange.vue'
import WidgetEditWorktimeDay from '@/components/dashboard/WidgetEditWorktimeDay.vue'
import WidgetContentTimeTrackings from '@/components/dashboard/WidgetContentTimeTrackings.vue'
import WidgetContentTodaysHours from '@/components/dashboard/WidgetContentTodaysHours.vue'
import WidgetShortcut from '@/components/dashboard/WidgetShortcut.vue'
import WidgetServiceRevenue from '@/components/dashboard/WidgetServiceRevenue.vue'
import type { GridStackWidget } from 'gridstack'
import WidgetYearRevenue from '@/components/dashboard/WidgetYearRevenue.vue'

type BaseStyle = Record<string, string | number>;

export interface NormalWidget {
  style?: BaseStyle;
  widgetTitle?: string;
  items?: number;
  visibleTypes?: string[];
  visibleState?: boolean
}

export interface ShortcutWidget {
  title: string;
  icon: string;
  route: string;
  text: string;
  style?: BaseStyle;
  type: string
}

export type DashboardWidget = NormalWidget | ShortcutWidget;

export type DashboardProperties = Record<string, DashboardWidget>;

export interface UseDashboard {
  isEditing: Ref<boolean>;
  isSaveAllowed: Ref<boolean>;
  isSaving: Ref<boolean>;
  defaultDashboard: GridStackWidget[] | null;
  defaultDashboardProperties: () => DashboardProperties;
  getWidgetName: (widgetObject: GridStackWidget) => string;
  getComponent: (name: string) => Component | null;
  getUserConfigDiff: (userConfig: DashboardProperties) => DashboardProperties;
  mergeDashboardConfig: (userConfig: DashboardProperties) => DashboardProperties;
}


const isEditing = ref(false)
const isSaveAllowed = ref(true)
const isSaving = ref(false)

let defaultDashboard: GridStackWidget[] | null = null


export function useDashboard(): UseDashboard {
  const { t } = useI18n()

  const titrop = useTitrop()

  const usersWidgets: GridStackWidget[] = [
    { id: 'favorites', x: 0, y: 0, h: 2, w: 12 },
    { id: 'infoboard', x: 0, y: 2, h: 8, w: 12 },
    { id: 'worktime', x: 0, y: 10, h: 11, w: 4 },
    { id: 'statechange', x: 4, y: 10, h: 11, w: 4 },
    { id: 'editworktime', x: 8, y: 10, h: 10, w: 4 },
    { id: 'shortcut-effort-capture', x: 0, y: 19, w: 4, sizeToContent: true },
    { id: 'shortcut-working-time-capture', x: 4, y: 19, w: 4, sizeToContent: true },
    { id: 'shortcut-infoboard-news', x: 8, y: 19, w: 4, sizeToContent: true },
    { id: 'shortcut-time-tracking', x: 0, y: 25, w: 4, sizeToContent: true },
    { id: 'shortcut-team-overview', x: 4, y: 25, w: 4, sizeToContent: true },
    { id: 'timetrackings', x: 8, y: 25, h: 10, w: 4 },
    { id: 'todayhours', x: 0, y: 30, h: 10.5, w: 4 }
  ]

  const managersWidgets: GridStackWidget[] = [
    { id: 'servicerevenue', x: 4, y: 40, h: 7, w: 4 },
    { id: 'yearrevenue', x: 8, y: 40, h: 5, w: 4 }
  ]

  if (!defaultDashboard) {
    defaultDashboard = [...usersWidgets]

    if (titrop.value.user.roles.department_leader)
      defaultDashboard.push(...managersWidgets)
  }

  const defaultDashboardProperties = (): DashboardProperties => {
    return {
      favorites: {
        style: { paddingLeft: '0.75rem', paddingRight: '0.75rem', paddingTop: '0.5rem' }
      },
      infoboard: {
        style: { overflowY: 'visible', paddingLeft: '0.75rem', paddingRight: '0.75rem' },
        widgetTitle: 'Infoboard',
        items: 10,
        visibleTypes: ['announce'],
        visibleState: false
      },
      worktime: {
        widgetTitle: 'Arbeitszeiterfassung',
        style: { paddingLeft: '0.75rem', paddingRight: '0.75rem' }
      },
      statechange: {
        widgetTitle: 'Mein Status',
        style: { paddingLeft: '0.75rem', paddingRight: '0.75rem' }
      },
      editworktime: {
        style: { overflowY: 'visible', fontSize: '0.875rem', lineHeight: '1.25rem', overflowX: 'visible' },
        widgetTitle: 'Arbeitszeitkorrekturen'
      },
      timetrackings: {
        widgetTitle: 'Aktive Time-Trackings',
        style: { paddingLeft: '0.75rem', paddingRight: '0.75rem' }
      },
      todayhours: {
        widgetTitle: 'Heutige Zeit',
        style: { paddingLeft: '0.75rem', paddingRight: '0.75rem' }
      },
      servicerevenue: {
        widgetTitle: 'Dienstleistungsumsatz',
        style: { paddingLeft: '1rem', paddingRight: '1rem' }
      },
      yearrevenue: {
        widgetTitle: 'Jahresumsatz',
        style: { paddingLeft: '1rem', paddingRight: '1rem' }
      },
      'shortcut-effort-capture': {
        title: t('Direct to effort capture'),
        icon: 'CaptureIcon',
        route: 'effort_capture',
        text: 'Erfassen Sie mühelos und präzise Ihre Leistungen. Verfolgen Sie Projekte, Arbeitsstunden und erreichte Meilensteine, um stets den Überblick über Ihre individuelle Performance zu behalten.',
        style: { paddingLeft: '0.75rem', paddingRight: '0.75rem', paddingTop: '0.25rem', paddingBottom: 0, border: 0, boxShadow: 'none' },
        type: 'full'
      },
      'shortcut-working-time-capture': {
        title: t('Worktime'),
        icon: 'StopwatchIcon',
        route: 'working-time-capture',
        text: 'Verpassen Sie keine Minute und optimieren Sie Ihre Zeitverwaltung, während Sie einfach und effizient Arbeitszeiten protokollieren.',
        style: { paddingLeft: '0.75rem', paddingRight: '0.75rem', paddingTop: '0.25rem', paddingBottom: 0, border: 0, boxShadow: 'none' },
        type: 'full'
      },
      'shortcut-infoboard-news': {
        title: t('Infoboard news'),
        icon: 'ChatIcon',
        route: 'infoboard-news',
        text: 'Bleiben Sie stets auf dem Laufenden mit dem Infoboard für die neuesten Einträge. Hier finden Sie aktuelle Informationen, Updates und relevante Ereignisse auf einen Blick – alles, was Sie wissen müssen, übersichtlich präsentiert.',
        style: {
          paddingLeft: '0.75rem', paddingRight: '0.75rem', paddingTop: '0.25rem', paddingBottom: 0, border: 0, boxShadow: 'none'
        },
        type: 'full'
      },
      'shortcut-time-tracking': {
        title: t('Time Tracking'),
        icon: 'TimeTrackingIcon',
        route: 'time-tracking',
        text: 'Ihre Stoppuhr im Netz, um präzise Zeiten für Ihre Leistungen zu erhalten. Erfassen Sie - mit oder ohne Verknüpfung zu einem Leistungsobjekt - die Zeit und überführen Sie diese mit Leichtigkeit direkt in eine erbrachte Leistung.',
        style: { paddingLeft: '0.75rem', paddingRight: '0.75rem', paddingTop: '0.25rem', paddingBottom: 0, border: 0, boxShadow: 'none' },
        type: 'full'
      },
      'shortcut-team-overview': {
        title: t('Employee overview'),
        icon: 'UsersIcon',
        route: 'team-overview',
        text: 'Erhalten Sie einen Überblick über Ihre Kollegen in der Mitarbeiterübersicht. Hier finden Sie alle relevanten Informationen zu Krankheitsstatus und Erreichbarkeit. Behalten Sie den Zustand Ihres Teams im Blick und optimieren Sie die Zusammenarbeit.',
        style:
            { paddingLeft: '0.75rem', paddingRight: '0.75rem', paddingTop: '0.25rem', paddingBottom: 0, border: 0, boxShadow: 'none' },
        type: 'full'
      }
    }
  }

  const widgetNames = {
    'favorites': 'Favoriten',
    'infoboard': 'Infoboard',
    'worktime': 'Arbeitszeiterfassung',
    'statechange': 'Mein Status',
    'editworktime': 'Arbeitszeitkorrekturen',
    'timetrackings': 'Aktive Time-Trackings',
    'todayhours': 'Heutige Zeit',
    'servicerevenue': 'Dienstleistungsumsatz',
    'yearrevenue': 'Jahresumsatz',
    'shortcut-effort-capture': t('Direct to effort capture'),
    'shortcut-working-time-capture': t('Worktime'),
    'shortcut-infoboard-news': t('Infoboard news'),
    'shortcut-time-tracking': t('Time Tracking'),
    'shortcut-team-overview': t('Employee overview')
  }
  const getWidgetName = (widgetObject: GridStackWidget) => {
    return widgetNames[widgetObject.id!]
  }

  const componentWrapper = {
    'favorites': WidgetFavorites,
    'infoboard': WidgetInfoboard,
    'worktime': WidgetContentWorkTime,
    'statechange': WidgetQuickStateChange,
    'editworktime': WidgetEditWorktimeDay,
    'timetrackings': WidgetContentTimeTrackings,
    'todayhours': WidgetContentTodaysHours,
    'servicerevenue': WidgetServiceRevenue,
    'yearrevenue': WidgetYearRevenue
  }
  const getComponent = (name: string) => {
    if (name.startsWith('shortcut')) {
      return WidgetShortcut
    }

    return componentWrapper[name] || null
  }

  const deepDiff = (
      obj1: DashboardWidget,
      obj2: DashboardWidget
  ): DashboardWidget => {
    const diff: DashboardWidget = {}

    for (const key in obj1) {
      if (!(key in obj2)) continue

      const val1 = obj1[key]
      const val2 = obj2[key]

      if (Array.isArray(val1) && Array.isArray(val2)) {
        const isEqual =
            val1.length === val2.length &&
            val1.every((v, i) => JSON.stringify(v) === JSON.stringify(val2[i]))

        if (!isEqual) {
          diff[key] = [...val1]
        }
        continue
      }

      if (
          typeof val1 === 'object' &&
          val1 !== null &&
          typeof val2 === 'object' &&
          val2 !== null
      ) {
        const nestedDiff = deepDiff(val1, val2)
        if (Object.keys(nestedDiff).length > 0) {
          diff[key] = nestedDiff
        }
        continue
      }

      if (val1 !== val2) {
        diff[key] = val1
      }
    }

    return diff
  }


  const getUserConfigDiff = (userConfig: DashboardProperties) => {
    const diff: DashboardProperties = {}

    for (const widgetKey in userConfig) {
      const userProps = userConfig[widgetKey]
      const defaultProps = defaultDashboardProperties()[widgetKey] || {}

      const widgetDiff = deepDiff(userProps, defaultProps)

      if (Object.keys(widgetDiff).length > 0) {
        diff[widgetKey] = widgetDiff
      }
    }

    return diff
  }

  const deepMergeWidget = (target: DashboardWidget, source: DashboardWidget): DashboardWidget => {
    const result: DashboardWidget = { ...target }

    for (const key in source) {
      const srcVal = source[key as keyof DashboardWidget]
      const tgtVal = target[key as keyof DashboardWidget]

      if (
          typeof srcVal === 'object' &&
          srcVal !== null &&
          !Array.isArray(srcVal) &&
          typeof tgtVal === 'object' &&
          tgtVal !== null &&
          !Array.isArray(tgtVal)
      ) {
        (result as any)[key] = { ...(tgtVal as object), ...(srcVal as object) }
      } else {
        (result as any)[key] = srcVal
      }
    }

    return result
  }

  const mergeDashboardConfig = (userConfig: DashboardProperties): DashboardProperties => {
    const merged: DashboardProperties = {}

    for (const widgetKey in defaultDashboardProperties()) {
      const defaultProps = defaultDashboardProperties()[widgetKey]
      const userProps = userConfig[widgetKey]

      if (userProps) {
        merged[widgetKey] = deepMergeWidget(defaultProps, userProps)
      } else {
        merged[widgetKey] = defaultProps
      }
    }

    for (const widgetKey in userConfig) {
      if (!merged[widgetKey]) {
        merged[widgetKey] = userConfig[widgetKey]
      }
    }

    return merged
  }

  return {
    isEditing,
    isSaveAllowed,
    isSaving,
    defaultDashboard,
    defaultDashboardProperties,
    getWidgetName,
    getComponent,
    getUserConfigDiff,
    mergeDashboardConfig
  }
}
